import React, { useState } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import InputField from "../../../components/InputField/InputField"
import GetSpace from "../../../utils/space"
import { faCalendar, faClose, faFilter } from "@fortawesome/free-solid-svg-icons"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import { useTranslation } from "react-i18next"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"

interface OrderAdvancedFiltersPopupProps {
  initialFilters: {
    dateStart: string
    dateEnd: string
    customerName: string
    customerDocument: string
    orderCode: string
    onlyOpenLostOrders: boolean
  }
  onApplyFilters: (filters: {
    dateStart: string
    dateEnd: string
    customerName: string
    customerDocument: string
    orderCode: string
    onlyOpenLostOrders: boolean
  }) => void
  onClose: () => void
}

export const OrderAdvancedFiltersPopup: React.FC<OrderAdvancedFiltersPopupProps> = ({
  initialFilters,
  onApplyFilters,
  onClose,
}) => {
  const { t } = useTranslation("order")

  const today = new Date().toInputDate()

  const [dateStart, setDateStart] = useState(initialFilters.dateStart)

  const [dateEnd, setDateEnd] = useState(initialFilters.dateEnd)

  const [customerName, setCustomerName] = useState(initialFilters.customerName)

  const [customerDocument, setCustomerDocument] = useState(initialFilters.customerDocument)

  const [orderCode, setOrderCode] = useState(initialFilters.orderCode)

  const [onlyOpenLostOrders, setOnlyOpenLostOrders] = useState(initialFilters.onlyOpenLostOrders)

  const openLostFilterOptions = [
    { label: t("label_filter_open_lost_all"), value: "all" },
    { label: t("label_filter_only_open_lost_orders"), value: "openLost" },
  ]

  const handleApply = () => {
    onApplyFilters({
      dateStart,
      dateEnd,
      customerName,
      customerDocument,
      orderCode,
      onlyOpenLostOrders,
    })
  }

  const handleClear = () => {
    const clearFilters = {
      dateStart: today,
      dateEnd: today,
      customerName: "",
      customerDocument: "",
      orderCode: "",
      onlyOpenLostOrders: false,
    }

    setDateStart(clearFilters.dateStart)

    setDateEnd(clearFilters.dateEnd)

    setCustomerName(clearFilters.customerName)

    setCustomerDocument(clearFilters.customerDocument)

    setOrderCode(clearFilters.orderCode)

    setOnlyOpenLostOrders(clearFilters.onlyOpenLostOrders)

    onApplyFilters(clearFilters)
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("title_advanced_filters")}
              icon={faFilter}
              boxShadow="none"
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    icon={faClose}
                    typeButton="button"
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.fluxo.order.orderAdvancedFiltersPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={5} />

            <div className="p-2 flex flex-col gap-3">
              <GenericDropDown
                data={openLostFilterOptions}
                initialState={onlyOpenLostOrders ? "openLost" : "all"}
                handlerFuncion={(event) => setOnlyOpenLostOrders(event.target.value === "openLost")}
                descriptionKey="interactionDescriptions:pages.fluxo.order.orderAdvancedFiltersPopup.select.select"
              />

              {!onlyOpenLostOrders && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <InputField
                      icon={faCalendar}
                      value={dateStart}
                      type={"date"}
                      placeholder={t("label_date_start")}
                      onChange={(e) => setDateStart(e.target.value)}
                      descriptionKey="interactionDescriptions:pages.fluxo.order.orderAdvancedFiltersPopup.input.dateStart"
                    />

                    <InputField
                      icon={faCalendar}
                      value={dateEnd}
                      type={"date"}
                      placeholder={t("label_date_end")}
                      onChange={(e) => setDateEnd(e.target.value)}
                      descriptionKey="interactionDescriptions:pages.fluxo.order.orderAdvancedFiltersPopup.input.dateEnd"
                    />
                  </div>

                  <InputField
                    value={customerName}
                    type={"text"}
                    placeholder={t("label_filter_customer_name")}
                    onChange={(e) => setCustomerName(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.fluxo.order.orderAdvancedFiltersPopup.input.filterCustomerName"
                  />

                  <InputField
                    value={customerDocument}
                    type={"text"}
                    placeholder={t("label_filter_customer_document")}
                    onChange={(e) => setCustomerDocument(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.fluxo.order.orderAdvancedFiltersPopup.input.filterCustomerDocument"
                  />

                  <InputField
                    value={orderCode}
                    type={"text"}
                    placeholder={t("label_filter_order_code")}
                    onChange={(e) => setOrderCode(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.fluxo.order.orderAdvancedFiltersPopup.input.filterOrderCode"
                  />
                </>
              )}
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className={defaultStyle.buttonsController}>
              <Button
                typeButton="button"
                type="primary"
                size="medium"
                bgColor="#EF4444"
                label={t("label_clear_filters")}
                onClick={handleClear}
                descriptionKey="interactionDescriptions:pages.fluxo.order.orderAdvancedFiltersPopup.button.clearFilters"
              />

              <GetSpace left={5} />

              <Button
                typeButton="button"
                type="primary"
                size="medium"
                bgColor={THEME.colors.buttonBGSaveColor}
                label={t("label_apply_filters")}
                onClick={handleApply}
                descriptionKey="interactionDescriptions:pages.fluxo.order.orderAdvancedFiltersPopup.button.applyFilters"
              />
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
